/**
 * @file
 * Views Exposed Form behaviors.
 *
 * Handles:
 * - Non-blocking loading indicator while an AJAX request is in flight.
 *   Filter controls are locked (via `inert` + `aria-busy`) only after
 *   Drupal starts the AJAX request — i.e. once form values have already
 *   been serialised and transmitted — so no filter values are ever
 *   excluded from submission by the guard.
 * - Replacing the default fullscreen throbber with a subtle progress bar
 *   at the top of the exposed form.
 * - Announcing the updated result count after exposed filter AJAX updates.
 * - Mobile filter panel accordion.
 */

import { BREAKPOINTS } from '../../src/variables.ts';
import { getInvestorMode, setInvestorMode } from '../../src/investor-mode.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Tracks whether the mobile filter panel was open before an AJAX refresh.
   * Allows the panel to re-open automatically after DOM replacement.
   */
  let mobilePanelWasOpen = false;

  /**
   * Set to true when a pager link is clicked so the next behavior attach
   * scrolls back to the top of the exposed-form wrapper.
   */
  let pendingPagerScroll = false;

  /**
   * Identity of the view whose pager was clicked, so the follow-up attach
   * scrolls the matching refreshed view rather than the first exposed form
   * on the page. `null` when the clicked pager had no resolvable view wrapper.
   */
  let pendingPagerScrollViewKey: string | null = null;

  /**
   * Desktop breakpoint media query — kept in sync with the SCSS `lg` breakpoint.
   */
  const desktopMq = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`);

  /**
   * Focusable elements selector for focus trapping inside the mobile panel.
   */
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Pending result-count announcement state for AJAX-updated exposed filters.
   */
  let pendingResultsAnnouncement = false;
  let pendingResultsViewKey: string | null = null;
  let pendingResultsResetTimer: number | null = null;
  let pendingResultsAnnounceTimer: number | null = null;

  /**
   * How long to keep an interaction eligible for an AJAX result announcement.
   */
  const PENDING_RESULTS_TIMEOUT = 10000;

  // ---------------------------------------------------------------------------
  // Result-count announcement helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the closest rendered Views wrapper for an exposed form.
   */
  function getViewWrapper(form: HTMLFormElement): HTMLElement | null {
    return form.closest<HTMLElement>('[data-tp-views-result-count]');
  }

  /**
   * Builds a stable key for a rendered Views wrapper.
   */
  function getViewKey(viewWrapper: HTMLElement): string | null {
    const domId = viewWrapper.dataset.tpViewsDomId;
    if (domId) {
      return `dom:${domId}`;
    }

    const viewId = viewWrapper.dataset.tpViewsId;
    const displayId = viewWrapper.dataset.tpViewsDisplayId;
    return viewId && displayId ? `view:${viewId}:${displayId}` : null;
  }

  /**
   * Parses the server-rendered result count from a Views wrapper.
   */
  function getResultCount(viewWrapper: HTMLElement): number | null {
    const rawCount = viewWrapper.dataset.tpViewsResultCount;
    if (!rawCount) {
      return null;
    }

    const count = Number.parseInt(rawCount, 10);
    return Number.isNaN(count) ? null : Math.max(0, count);
  }

  /**
   * Finds a rendered Views wrapper whose stable key matches the given key.
   *
   * The key (derived from `data-tp-views-dom-id`, or view id + display id)
   * survives AJAX DOM replacement, so it reliably re-identifies the same view
   * after its content is refreshed.
   */
  function findViewWrapperByKey(viewKey: string | null): HTMLElement | null {
    if (!viewKey) {
      return null;
    }

    const viewWrappers = Array.from(document.querySelectorAll<HTMLElement>('[data-tp-views-result-count]'));
    return viewWrappers.find((viewWrapper) => getViewKey(viewWrapper) === viewKey) ?? null;
  }

  /**
   * Finds the most relevant Views wrapper for the pending announcement.
   */
  function findPendingViewWrapper(): HTMLElement | null {
    const matchingWrapper = findViewWrapperByKey(pendingResultsViewKey);
    if (matchingWrapper) {
      return matchingWrapper;
    }

    const viewWrappers = Array.from(document.querySelectorAll<HTMLElement>('[data-tp-views-result-count]'));
    return viewWrappers.length === 1 ? viewWrappers[0] : null;
  }

  /**
   * Clears pending result-count announcement state.
   */
  function clearPendingResultsAnnouncement(): void {
    pendingResultsAnnouncement = false;
    pendingResultsViewKey = null;

    if (pendingResultsResetTimer !== null) {
      window.clearTimeout(pendingResultsResetTimer);
      pendingResultsResetTimer = null;
    }
  }

  /**
   * Marks that the next AJAX-rendered result count should be announced.
   */
  function markResultsAnnouncementPending(form: HTMLFormElement): void {
    const viewWrapper = getViewWrapper(form);
    pendingResultsAnnouncement = true;
    pendingResultsViewKey = viewWrapper ? getViewKey(viewWrapper) : null;

    if (pendingResultsResetTimer !== null) {
      window.clearTimeout(pendingResultsResetTimer);
    }

    pendingResultsResetTimer = window.setTimeout(clearPendingResultsAnnouncement, PENDING_RESULTS_TIMEOUT);
  }

  /**
   * Builds the localized result-count announcement message.
   */
  function getResultsAnnouncementMessage(count: number): string {
    if (count === 0) {
      return Drupal.t('No results found');
    }

    return Drupal.formatPlural(count, '1 result found', '@count results found', { '@count': count });
  }

  /**
   * Announces the pending result count once the AJAX-rendered view is available.
   */
  function announcePendingResults(): void {
    if (!pendingResultsAnnouncement) {
      return;
    }

    const viewWrapper = findPendingViewWrapper();
    if (!viewWrapper) {
      return;
    }

    const count = getResultCount(viewWrapper);
    if (count === null) {
      return;
    }

    const message = getResultsAnnouncementMessage(count);
    const status = viewWrapper.querySelector<HTMLElement>('[data-views-exposed-form-results-status]');

    clearPendingResultsAnnouncement();

    if (!status) {
      Drupal.announce(message, 'polite');
      return;
    }

    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /**
   * Schedules the pending result-count announcement after behavior attachment.
   */
  function schedulePendingResultsAnnouncement(): void {
    if (!pendingResultsAnnouncement) {
      return;
    }

    if (pendingResultsAnnounceTimer !== null) {
      window.clearTimeout(pendingResultsAnnounceTimer);
    }

    pendingResultsAnnounceTimer = window.setTimeout(() => {
      pendingResultsAnnounceTimer = null;
      announcePendingResults();
    }, 50);
  }

  /**
   * Determines whether an event target is an exposed form control that can
   * trigger a Views reload or AJAX update.
   */
  function isReloadTriggerControl(
    target: EventTarget | null
  ): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    if (target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) {
      return true;
    }

    if (!(target instanceof HTMLInputElement)) {
      return false;
    }

    return target.type !== 'hidden';
  }

  /**
   * Determines whether a clicked control can submit or reset exposed filters.
   */
  function isSubmitOrResetTrigger(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const control = target.closest('button, input');
    if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) {
      return false;
    }

    return ['submit', 'reset', 'image'].includes(control.type);
  }

  /**
   * Tracks user-initiated filter updates that may trigger Views AJAX.
   */
  function watchResultAnnouncements(form: HTMLFormElement): void {
    const markPending = () => markResultsAnnouncementPending(form);

    form.addEventListener('submit', markPending, true);

    form.addEventListener(
      'change',
      (event) => {
        if (isReloadTriggerControl(event.target)) {
          markPending();
        }
      },
      true
    );

    form.addEventListener(
      'input',
      (event) => {
        if (isReloadTriggerControl(event.target)) {
          markPending();
        }
      },
      true
    );

    form.addEventListener(
      'click',
      (event) => {
        if (isSubmitOrResetTrigger(event.target)) {
          markPending();
        }
      },
      true
    );
  }

  // ---------------------------------------------------------------------------
  // Inline progress-bar helpers
  // ---------------------------------------------------------------------------

  /**
   * Creates a non-blocking progress bar element at the top of the form.
   */
  function createProgressBar(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'views-exposed-form__progress';
    wrapper.setAttribute('role', 'status');

    const bar = document.createElement('span');
    bar.className = 'views-exposed-form__progress-bar';
    bar.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(bar);

    const srText = document.createElement('span');
    srText.className = 'visually-hidden';
    srText.textContent = Drupal.t('Updating results');
    wrapper.appendChild(srText);

    return wrapper;
  }

  /**
   * Shows the non-blocking progress bar and removes any fullscreen
   * throbber Drupal may have added.
   */
  function showProgress(form: HTMLFormElement): void {
    let progress = form.querySelector<HTMLElement>('.views-exposed-form__progress');
    if (!progress) {
      progress = createProgressBar();
      form.prepend(progress);
    }
    progress.hidden = false;

    // Remove Drupal's default fullscreen throbber so it doesn't block the page.
    document.querySelectorAll('.ajax-progress-fullscreen, .ajax-progress--fullscreen').forEach((el) => el.remove());
  }

  /**
   * Hides the progress bar.
   */
  function hideProgress(form: HTMLFormElement): void {
    const progress = form.querySelector<HTMLElement>('.views-exposed-form__progress');
    if (progress) {
      progress.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Non-blocking loading indicator
  // ---------------------------------------------------------------------------

  /**
   * Selector for every filter container that must be locked during loading.
   *
   * Setting `inert` on these containers blocks both pointer **and** keyboard
   * interaction without visually disabling individual controls, so the guard
   * is consistent across all input methods.
   */
  const LOADING_LOCK_SELECTOR = [
    '.views-exposed-form__filters',
    '.views-exposed-form__mobile-body',
    '.views-exposed-form__sort',
    '.active-filters',
  ].join(', ');

  /**
   * Adds a visual loading modifier, shows the progress bar, and locks filter
   * containers against both pointer and keyboard interaction.
   *
   * `aria-busy="true"` on the form element exposes the busy state to assistive
   * technologies. `inert` on each container prevents any further interaction
   * (pointer or keyboard) while the AJAX request is in flight, without
   * changing the visual appearance of the controls.
   */
  function setFormLoading(form: HTMLFormElement): void {
    form.classList.add('views-exposed-form--loading');
    form.setAttribute('aria-busy', 'true');
    form.querySelectorAll<HTMLElement>(LOADING_LOCK_SELECTOR).forEach((el) => {
      el.inert = true;
    });
    showProgress(form);
  }

  /**
   * Removes the loading modifier, hides the progress bar, and restores full
   * interactivity to filter containers.
   */
  function clearFormLoading(form: HTMLFormElement): void {
    form.classList.remove('views-exposed-form--loading');
    form.removeAttribute('aria-busy');
    form.querySelectorAll<HTMLElement>(LOADING_LOCK_SELECTOR).forEach((el) => {
      el.inert = false;
    });
    hideProgress(form);
  }

  // ---------------------------------------------------------------------------
  // Non-blocking AJAX loading state
  // ---------------------------------------------------------------------------

  /**
   * Manages the non-blocking loading indicator for Views AJAX requests.
   *
   * `setFormLoading` is called only after Drupal has started the AJAX
   * request (when its fullscreen throbber appears). At that point, form
   * values have already been serialised and sent, so setting `inert` on
   * filter containers is safe — no submitted value can be excluded.
   * `aria-busy="true"` on the form element informs assistive technologies
   * that content is being updated.
   */
  Drupal.behaviors.tpViewsExposedFormLoading = {
    attach(context: Document | HTMLElement) {
      // Clear loading state globally after every AJAX round-trip.
      document.querySelectorAll<HTMLFormElement>('.views-exposed-form--loading').forEach(clearFormLoading);

      // Safety net: hide any progress bar that survived an AJAX DOM replacement.
      document.querySelectorAll<HTMLElement>('.views-exposed-form__progress:not([hidden])').forEach((el) => {
        el.hidden = true;
      });

      // Safety net: remove any orphaned `inert` markers on filter containers
      // that may have survived an AJAX DOM replacement on a form that is no
      // longer marked as loading (e.g. the form was replaced mid-request).
      document
        .querySelectorAll<HTMLElement>(
          `.views-exposed-form:not(.views-exposed-form--loading) ${LOADING_LOCK_SELECTOR.split(', ')
            .map((s) => s.trim())
            .join(', .views-exposed-form:not(.views-exposed-form--loading) ')}`
        )
        .forEach((el) => {
          if (el.inert) {
            el.inert = false;
          }
        });

      schedulePendingResultsAnnouncement();

      // Scroll to the top of the refreshed view's exposed-form wrapper after
      // pager navigation. Scoped to the view whose pager was clicked so pages
      // with multiple AJAX views do not jump to an unrelated exposed form.
      if (pendingPagerScroll) {
        pendingPagerScroll = false;
        const viewKey = pendingPagerScrollViewKey;
        pendingPagerScrollViewKey = null;

        const viewWrapper = findViewWrapperByKey(viewKey);
        const formWrapper = viewWrapper?.querySelector<HTMLElement>('.views-exposed-form__wrapper') ?? null;

        // Only scroll when the matching refreshed view is found; otherwise the
        // pending state is simply cleared above.
        if (formWrapper) {
          formWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      // One-time delegated listener on <body> to detect pager link clicks.
      once('tp-pager-scroll-listener', 'body').forEach(() => {
        document.addEventListener(
          'click',
          (e) => {
            if (!(e.target instanceof Element)) {
              return;
            }

            const pagerLink = e.target.closest('.js-pager__items a');
            if (!pagerLink) {
              return;
            }

            pendingPagerScroll = true;

            // Capture the clicked view's identity so the follow-up attach
            // scrolls the matching refreshed view, not the first one found.
            const viewWrapper = pagerLink.closest<HTMLElement>('[data-tp-views-result-count]');
            pendingPagerScrollViewKey = viewWrapper ? getViewKey(viewWrapper) : null;
          },
          true
        );
      });

      // One-time setup per form: install the MutationObserver to intercept
      // Drupal's fullscreen throbber and replace it with our subtle indicator.
      // This is the sole trigger for setFormLoading — it fires only after
      // Drupal has already serialised and dispatched the AJAX request, so
      // setting `inert` on filter containers at this point is safe and no
      // submitted form value can be excluded.
      once('tp-views-exposed-form-loading', '.views-exposed-form', context).forEach((el) => {
        const form = el as HTMLFormElement;

        watchResultAnnouncements(form);

        // ── Keyboard guard ──────────────────────────────────────────────────
        // `pointer-events: none` (CSS) has no effect on keyboard input.
        // This capture-phase keydown listener blocks the specific keystrokes
        // that change filter values — Space on checkboxes/radios, and
        // ArrowUp/ArrowDown/Space/Enter on selects — whenever the form is in
        // its loading state. `preventDefault()` stops the browser's default
        // action (toggling / changing the value) before any `change` event
        // fires, so no submitted form value is affected.
        // Tab is intentionally NOT blocked so keyboard users can still
        // navigate around the form and read its current state.
        form.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (!form.classList.contains('views-exposed-form--loading')) return;

            const target = event.target as HTMLElement;

            // Checkboxes and radios: Space toggles the checked state.
            if (
              target instanceof HTMLInputElement &&
              (target.type === 'checkbox' || target.type === 'radio') &&
              event.key === ' '
            ) {
              event.preventDefault();
              event.stopImmediatePropagation();
              return;
            }

            // Native selects: arrow keys and Space/Enter change the selection.
            if (
              target instanceof HTMLSelectElement &&
              (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === ' ' || event.key === 'Enter')
            ) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          true
        );

        // ── Class → aria-busy sync ──────────────────────────────────────────
        // `bef-dropdown.ts` adds the loading class immediately on filter
        // change (before the debounce fires), but `setFormLoading` — which
        // sets `aria-busy` — only runs once the AJAX throbber appears.
        // A MutationObserver on the form's class attribute closes that gap,
        // ensuring assistive technologies receive the busy signal at the same
        // moment the visual overlay appears.
        const classObserver = new MutationObserver(() => {
          if (form.classList.contains('views-exposed-form--loading')) {
            form.setAttribute('aria-busy', 'true');
          } else {
            form.removeAttribute('aria-busy');
          }
        });
        classObserver.observe(form, { attributes: true, attributeFilter: ['class'] });

        // ── Throbber interception / full guard ──────────────────────────────
        // Once Drupal's AJAX request is in flight its fullscreen throbber
        // appears. We remove it and call setFormLoading, which additionally
        // sets `inert` on filter containers — safe at this point because form
        // values have already been serialised and transmitted.
        const bodyObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (
                node instanceof HTMLElement &&
                (node.classList.contains('ajax-progress-fullscreen') ||
                  node.classList.contains('ajax-progress--fullscreen'))
              ) {
                node.remove();
                setFormLoading(form);
              }
            }
          }
        });

        bodyObserver.observe(document.body, { childList: true });
      });
    },

    detach(context: Document | HTMLElement, _settings: unknown, trigger: string) {
      if (trigger === 'unload') {
        const root = context === document ? document.body : (context as HTMLElement);
        root.querySelectorAll<HTMLFormElement>('.views-exposed-form--loading').forEach(clearFormLoading);
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Focus trap helper
  // ---------------------------------------------------------------------------

  /**
   * Traps keyboard focus inside a container element (dialog).
   * Returns a cleanup function that removes the event listener.
   */
  function trapFocus(container: HTMLElement): () => void {
    function handleKeydown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;

      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeydown);
    return () => container.removeEventListener('keydown', handleKeydown);
  }

  // ---------------------------------------------------------------------------
  // Mobile filter accordion
  // ---------------------------------------------------------------------------

  /**
   * Mobile filter accordion behavior for Views Exposed Form.
   */
  Drupal.behaviors.tpViewsExposedFormMobileAccordion = {
    attach(context: Document | HTMLElement) {
      once('tp-views-exposed-form-mobile-accordion', '.views-exposed-form', context).forEach((el) => {
        const form = el as HTMLFormElement;
        const mobileToggle = form.querySelector<HTMLButtonElement>('.views-exposed-form__mobile-toggle');
        const mobilePanel = form.querySelector<HTMLDivElement>('.views-exposed-form__mobile-panel');
        const mobileBody = form.querySelector<HTMLDivElement>('.views-exposed-form__mobile-body');
        const mobileClose = form.querySelector<HTMLButtonElement>('.views-exposed-form__mobile-close');
        const filtersContainer = form.querySelector<HTMLDivElement>('.views-exposed-form__filters');
        const filterBox = form.querySelector<HTMLDivElement>('.views-exposed-form__filter-box');
        const badge = form.querySelector<HTMLSpanElement>('.views-exposed-form__mobile-toggle-badge');

        if (!mobileToggle || !mobilePanel || !mobileBody || !mobileClose || !filtersContainer || !filterBox || !badge) {
          return;
        }

        const toggle = mobileToggle;
        const panel = mobilePanel;
        const body = mobileBody;
        const closeBtn = mobileClose;
        const filters = filtersContainer;
        const badgeEl = badge;

        let filtersInMobile = false;
        let removeFocusTrap: (() => void) | null = null;

        function moveFiltersToMobile() {
          if (!filtersInMobile) {
            body.appendChild(filters);
            filtersInMobile = true;
          }
        }

        function moveFiltersToDesktop() {
          if (filtersInMobile) {
            filterBox?.insertBefore(filters, filterBox.querySelector('.active-filters'));
            filtersInMobile = false;
          }
        }

        function openPanel(silent = false) {
          moveFiltersToMobile();
          panel.hidden = false;
          panel.classList.toggle('views-exposed-form__mobile-panel--animate', !silent);
          toggle.setAttribute('aria-expanded', 'true');
          mobilePanelWasOpen = true;

          body.querySelectorAll<HTMLElement>('[data-bef-dropdown]').forEach((dropdown) => {
            const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-dropdown__trigger');
            const befPanel = dropdown.querySelector<HTMLElement>('.bef-dropdown__panel');
            if (trigger && befPanel) {
              trigger.setAttribute('aria-expanded', 'true');
              befPanel.classList.remove('bef-dropdown__panel--hidden');
            }
          });

          body.querySelectorAll<HTMLElement>('[data-bef-number-dropdown]').forEach((dropdown) => {
            const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-number-dropdown__trigger');
            const befPanel = dropdown.querySelector<HTMLElement>('.bef-number-dropdown__panel');
            if (trigger && befPanel) {
              trigger.setAttribute('aria-expanded', 'true');
              befPanel.classList.remove('bef-number-dropdown__panel--hidden');
            }
          });

          removeFocusTrap = trapFocus(panel);

          if (!silent) {
            setTimeout(() => closeBtn.focus(), 0);
          }
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
        }

        function closePanel() {
          panel.hidden = true;
          panel.classList.remove('views-exposed-form__mobile-panel--animate');
          toggle.setAttribute('aria-expanded', 'false');
          mobilePanelWasOpen = false;

          if (removeFocusTrap) {
            removeFocusTrap();
            removeFocusTrap = null;
          }

          toggle.focus();
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
        }

        /**
         * Update the mobile badge count from server-rendered chips.
         */
        function updateBadge() {
          const count = form.querySelectorAll('.active-filters__chip').length;
          if (count > 0) {
            badgeEl.textContent = String(count);
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        }

        toggle.addEventListener('click', () => {
          openPanel();
        });

        closeBtn.addEventListener('click', closePanel);

        panel.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            closePanel();
          }
        });

        // Badge is already set server-side in Twig, but keep JS sync
        // for any client-side changes.
        form.addEventListener('change', updateBadge);

        function handleBreakpointChange() {
          if (desktopMq.matches) {
            if (!panel.hidden) {
              closePanel();
            }
            moveFiltersToDesktop();
          } else if (!panel.hidden) {
            moveFiltersToMobile();
          }
        }
        desktopMq.addEventListener('change', handleBreakpointChange);

        if (!desktopMq.matches && mobilePanelWasOpen) {
          openPanel(true);
        } else {
          handleBreakpointChange();
        }
      });
    },
  };

  // ---------------------------------------------------------------------------
  // Investor mode filter sync
  // ---------------------------------------------------------------------------

  /**
   * Syncs the investor mode exposed filter with session cookie.
   *
   * @description
   * On attach, reads the INVESTOR_MODE_KEY from session cookie and
   * checks/unchecks the filter switch accordingly. If the state differs
   * from the server-rendered value, dispatches a change event so BEF
   * autosubmit re-fetches results. User-initiated toggles are written
   * back to session cookie so other pages (e.g. property detail) stay
   * in sync.
   */
  Drupal.behaviors.tpViewsExposedFormInvestorSync = {
    attach(context: Document | HTMLElement) {
      once('tp-investor-filter-sync', 'input[name="field_is_investor"]', context).forEach((el) => {
        const checkbox = el as HTMLInputElement;
        const stored = getInvestorMode();

        const urlParams = new URLSearchParams(window.location.search);
        const investorParam = urlParams.get('field_is_investor');

        // Override what is stored if it's set as a query param.
        if (investorParam !== null) {
          // Cast to boolean: '1', 'true' → true; '0', 'false', '' → false
          setInvestorMode(investorParam !== '0' && investorParam !== 'false' && investorParam !== '');
        } else if (stored === null) {
          // No stored preference — keep the server-rendered / URL state
          // and seed the cookie so future pages stay in sync.
          setInvestorMode(checkbox.checked);
        } else {
          const active = stored;

          // Sync cookie → checkbox; trigger form reload only when
          // the stored preference disagrees with the server-rendered state.
          if (checkbox.checked !== active) {
            checkbox.checked = active;

            // Capture scroll position before BEF autosubmit fires the AJAX
            // request that ends with a viewsScrollTop command. Once the AJAX
            // loading indicator is cleared (round-trip complete), restore the
            // captured position so the page does not jump down to the view.
            const savedScrollY = window.scrollY;
            const form = checkbox.closest<HTMLFormElement>('.views-exposed-form');
            if (form) {
              const observer = new MutationObserver(() => {
                if (!form.classList.contains('views-exposed-form--loading')) {
                  observer.disconnect();
                  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
                }
              });
              observer.observe(form, { attributes: true, attributeFilter: ['class'] });
              // Safety net: disconnect after 5 s in case the loading class
              // is never applied (e.g. BEF uses a different throbber path).
              setTimeout(() => observer.disconnect(), 5000);
            }

            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Sync checkbox → cookie on user interaction.
        checkbox.addEventListener('change', () => {
          setInvestorMode(checkbox.checked);
        });
      });
    },
  };
})(Drupal, once);
